import type { GuidedFlow } from './types';

export const GUIDED_FLOWS: Record<string, GuidedFlow> = {
  connect_exchange: {
    id: 'connect_exchange',
    title: 'Connect an Exchange',
    steps: [
      {
        id: 'select_exchange',
        prompt: 'Which exchange would you like to connect?',
        expectedType: 'choice',
        choices: ['Binance', 'Coinbase', 'Kraken', 'Bybit', 'OKX', 'KuCoin'],
        nextStepFn: () => 'has_keys',
      },
      {
        id: 'has_keys',
        prompt: 'Do you have your API keys ready?',
        expectedType: 'choice',
        choices: ['Yes', 'No'],
        nextStepFn: (response: string) =>
          response.toLowerCase() === 'yes' ? 'connect_steps' : 'create_keys',
      },
      {
        id: 'create_keys',
        prompt: `Here's how to create API keys:\n\n1. Log in to your exchange account\n2. Go to **API Management** (usually in account settings)\n3. Create a new API key\n4. Enable **Read** and **Trade** permissions\n5. **Do NOT** enable Withdraw permissions\n6. Consider adding IP whitelist restrictions\n7. Copy both the **API Key** and **API Secret**\n\nOnce you have your keys, come back and we'll connect your exchange.`,
        expectedType: 'confirm',
        choices: ['I have my keys now', 'I need more help'],
        nextStepFn: (response: string) =>
          response.toLowerCase().includes('keys') ? 'connect_steps' : null,
      },
      {
        id: 'connect_steps',
        prompt: `Great! Here's how to connect:\n\n1. Go to **Connections** in the sidebar\n2. Click **"Add Connection"**\n3. Select your exchange\n4. Paste your **API Key** and **API Secret**\n5. Click **"Test Connection"** to verify\n6. Save the connection\n\n**Security tip:** Use a separate API key for Rugira with minimal permissions (Read + Trade only).`,
        expectedType: 'confirm',
        choices: ['Done', 'I need help'],
        nextStepFn: () => null,
      },
    ],
  },
  diagnose_bot_stopped: {
    id: 'diagnose_bot_stopped',
    title: 'Diagnose Stopped Agent',
    steps: [
      {
        id: 'check_status',
        prompt: `Let me check your agent status...\n\nI found a stopped agent. Here are common causes:\n\n1. **API Key Expired** -- Your exchange API key may need renewal\n2. **Insufficient Balance** -- Not enough funds for the configured position size\n3. **Compliance Hold** -- A risk or compliance rule triggered a halt\n4. **Exchange Rate Limit** -- Too many API requests in a short time\n5. **Network Issues** -- Temporary connectivity problems\n\nCheck the **error message** shown on the agent's detail page for specifics.`,
        expectedType: 'choice',
        choices: ['That helped', 'Still need help', 'Check my alerts'],
        nextStepFn: (response: string) => {
          if (response.toLowerCase().includes('alert')) return 'check_alerts';
          if (response.toLowerCase().includes('still') || response.toLowerCase().includes('help')) return 'more_help';
          return null;
        },
      },
      {
        id: 'check_alerts',
        prompt: `Here are your recent alerts that may be related:\n\n- **[error]** Agent Halted: ETH Momentum Beta stopped due to insufficient API permissions\n- **[warning]** High Drawdown: BTC Grid Alpha approaching max drawdown threshold\n\nTo resolve API permission issues:\n1. Go to your exchange and check the API key permissions\n2. Ensure **Trade** permission is enabled\n3. Update the key in **Connections** if needed\n4. Review and restart the agent from **Agents**`,
        expectedType: 'confirm',
        choices: ['Got it', 'Contact support'],
        nextStepFn: () => null,
      },
      {
        id: 'more_help',
        prompt: `For further assistance:\n\n1. Check the **Monitoring** page for detailed system status\n2. Review the agent's **decision trail** in the Agent Control Room\n3. Try restarting the agent in **Paper Mode** first to test\n4. If the issue persists, contact **support@rugira.ch** with:\n   - Agent name and ID\n   - Error message\n   - Approximate time of failure`,
        expectedType: 'confirm',
        choices: ['Thanks'],
        nextStepFn: () => null,
      },
    ],
  },
  explain_order_failure: {
    id: 'explain_order_failure',
    title: 'Explain Order Failure',
    steps: [
      {
        id: 'show_orders',
        prompt: `Let me check your recent orders for failures...\n\nI found some orders that may have issues. Common order failure reasons:\n\n1. **Insufficient Funds** -- Not enough balance on the exchange\n2. **Invalid Price** -- Price outside exchange limits\n3. **Rate Limited** -- Too many orders in a short period\n4. **Market Closed** -- Trading pair temporarily unavailable\n5. **Position Limit** -- Maximum position size reached`,
        expectedType: 'choice',
        choices: ['Explain more', 'How to fix', 'Contact support'],
        nextStepFn: (response: string) => {
          if (response.toLowerCase().includes('fix')) return 'fix_steps';
          if (response.toLowerCase().includes('support')) return 'contact_support';
          return 'details';
        },
      },
      {
        id: 'details',
        prompt: `**Order Failure Details:**\n\n**What happened:** The order could not be executed on the exchange.\n**Impact:** The agent's intended trade was not completed. This may affect your strategy's performance.\n**Next step:** Check your exchange balance and API permissions, then review the agent's risk settings.`,
        expectedType: 'confirm',
        choices: ['Got it'],
        nextStepFn: () => null,
      },
      {
        id: 'fix_steps',
        prompt: `**How to fix order failures:**\n\n1. **Check Exchange Balance** -- Ensure sufficient funds in your trading account\n2. **Review Position Limits** -- Adjust max position size in agent settings\n3. **Check API Permissions** -- Verify Trade permission is enabled\n4. **Reduce Order Frequency** -- If rate-limited, increase the agent's order interval\n5. **Restart in Paper Mode** -- Test the fix without risking real funds`,
        expectedType: 'confirm',
        choices: ['Thanks'],
        nextStepFn: () => null,
      },
      {
        id: 'contact_support',
        prompt: `To contact support about order failures:\n\n**Email:** support@rugira.ch\n**Include:**\n- Order ID (from the Orders page)\n- Agent name and strategy\n- Error message (if any)\n- Approximate time of failure\n\nOur team typically responds within 4 business hours.`,
        expectedType: 'confirm',
        choices: ['Thanks'],
        nextStepFn: () => null,
      },
    ],
  },
};

export function getGuidedFlow(flowId: string): GuidedFlow | undefined {
  return GUIDED_FLOWS[flowId];
}

export function getFlowStep(flowId: string, stepId: string) {
  const flow = GUIDED_FLOWS[flowId];
  if (!flow) return undefined;
  return flow.steps.find(s => s.id === stepId);
}
